import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfMake.vfs = pdfFonts.vfs;

const toBase64 = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.setAttribute("crossOrigin", "anonymous");
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const d = new Date(dateString);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const generateIssueMTCPdf = async (row) => {
  let headerImg = null;
  let footerImg = null;

  try {
    headerImg = await toBase64("/header.png");
  } catch {
    headerImg = null;
  }

  try {
    footerImg = await toBase64("/footer.png");
  } catch {
    footerImg = null;
  }

  /* ──────────────────────────────────────────────────────────── */
  /*  TWO-COLUMN KEY-VALUE PAIRS                                  */
  /* ──────────────────────────────────────────────────────────── */
  const leftDetails = [
    ["Division", row.division_name || "N/A"],
    ["Item Name", row.item_name || "N/A"],
    ["IBR Form No", row.ibr_form_no || "N/A"],
    ["Total Qty", String(row.total_qty ?? 0)],
  ];

  const rightDetails = [
    ["Project", row.project_name || "N/A"],
    ["Drawing No", row.drawing_no || "N/A"],
    ["Total Items", String(row.items_count ?? 0)],
    ["Created Date", formatDate(row.created_at)],
  ];

  const maxRows = Math.max(leftDetails.length, rightDetails.length);
  while (leftDetails.length < maxRows) leftDetails.push(["", ""]);
  while (rightDetails.length < maxRows) rightDetails.push(["", ""]);

  const kvBody = [];
  for (let i = 0; i < maxRows; i++) {
    kvBody.push([
      { text: leftDetails[i][0], style: "labelCell" },
      { text: leftDetails[i][1], style: "valueCell" },
      { text: rightDetails[i][0], style: "labelCell" },
      { text: rightDetails[i][1], style: "valueCell" },
    ]);
  }

  const keyValueTable = {
    table: {
      headerRows: 0,
      widths: [90, "*", 90, "*"],
      body: kvBody,
    },
    layout: {
      fillColor: (rowIndex) => (rowIndex % 2 === 0 ? "#f5f5f5" : null),
      hLineWidth: () => 0.4,
      vLineWidth: () => 0.4,
      hLineColor: () => "#ccc",
      vLineColor: () => "#ccc",
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingTop: () => 5,
      paddingBottom: () => 5,
    },
  };

  /* ──────────────────────────────────────────────────────────── */
  /*  ITEMS TABLE — S.No | TC No. | Material | Grade | Qty       */
  /* ──────────────────────────────────────────────────────────── */
  let itemsSection = [];

  if (Array.isArray(row.items) && row.items.length > 0) {
    const itemHeaders = [
      { text: "S.No", style: "itemHeader" },
      { text: "TC No.", style: "itemHeader" },
      { text: "Material", style: "itemHeader" },
      { text: "Grade", style: "itemHeader" },
      { text: "Qty", style: "itemHeader" },
    ];

    // ✅ Using CORRECT field names from your API
    const itemRows = row.items.map((item, idx) => [
      { text: String(idx + 1), style: "itemCell" },
      { text: item.tc_no_display || "—", style: "itemCell" },
      { text: item.material || "—", style: "itemCell" },
      { text: item.grade || "—", style: "itemCell" },
      { text: String(item.qty ?? "—"), style: "itemCell" },
    ]);

    // Total row
    const totalQty = row.items.reduce(
      (sum, i) => sum + (parseFloat(i.qty) || 0),
      0
    );

    const totalRow = [
      { text: "", style: "itemCell" },
      { text: "", style: "itemCell" },
      { text: "", style: "itemCell" },
      {
        text: "Total",
        bold: true,
        fontSize: 9,
        alignment: "center",
        fillColor: "#e3f2fd",
      },
      {
        text: String(totalQty),
        bold: true,
        fontSize: 9,
        alignment: "center",
        fillColor: "#e3f2fd",
      },
    ];

    itemsSection = [
      { text: "\n" },
      {
        text: "Material / Item Details",
        style: "sectionTitle",
        margin: [0, 10, 0, 6],
      },
      {
        table: {
          headerRows: 1,
          widths: [40, "*", "*", "*", 60],
          body: [itemHeaders, ...itemRows, totalRow],
        },
        layout: {
          fillColor: (rowIndex) => {
            if (rowIndex === 0) return "#1976d2";
            return rowIndex % 2 === 0 ? "#fafafa" : null;
          },
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => "#bbb",
          vLineColor: () => "#bbb",
          paddingLeft: () => 6,
          paddingRight: () => 6,
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
      },
    ];
  }

  /* ──────────────────────────────────────────────────────────── */
  /*  DOCUMENT DEFINITION                                         */
  /* ──────────────────────────────────────────────────────────── */
  const docDefinition = {
    pageSize: "A4",
    pageMargins: [40, headerImg ? 100 : 60, 40, footerImg ? 100 : 60],

    header: () => {
      if (!headerImg) return [];
      return [
        {
          columns: [
            { width: "*", text: "" },
            {
              image: headerImg,
              width: 500,
              height: 60,
              alignment: "center",
            },
            { width: "*", text: "" },
          ],
          margin: [0, 10, 0, 0],
        },
      ];
    },

    footer: (currentPage, pageCount) => {
      const content = [
        {
          canvas: [
            {
              type: "line",
              x1: 40,
              y1: 0,
              x2: 555,
              y2: 0,
              lineWidth: 1.5,
              lineColor: "#1976d2",
            },
          ],
          margin: [0, 0, 0, 4],
        },
      ];

      if (footerImg) {
        content.push({
          columns: [
            { width: "*", text: "" },
            {
              image: footerImg,
              width: 400,
              height: 50,
              alignment: "center",
            },
            { width: "*", text: "" },
          ],
        });
      }

      content.push({
        text: `Page ${currentPage} of ${pageCount}`,
        alignment: "center",
        fontSize: 7,
        color: "#999",
        margin: [0, 2, 0, 0],
      });

      return content;
    },

    content: [
      {
        text: "ISSUE MTC REPORT",
        style: "title",
        alignment: "center",
        margin: [0, 0, 0, 4],
      },
      {
        text: "Generated on: " + new Date().toLocaleString("en-GB"),
        alignment: "center",
        fontSize: 8,
        color: "#777",
        margin: [0, 0, 0, 14],
      },
      keyValueTable,
      ...itemsSection,
    ],

    styles: {
      title: { fontSize: 16, bold: true, color: "#1976d2" },
      sectionTitle: { fontSize: 12, bold: true, color: "#333" },
      labelCell: { fontSize: 10, bold: true, color: "#333" },
      valueCell: { fontSize: 10, color: "#555" },
      itemHeader: {
        fontSize: 9,
        bold: true,
        color: "#ffffff",
        alignment: "center",
      },
      itemCell: { fontSize: 9, color: "#333", alignment: "center" },
    },

    info: {
      title: "Issue_MTC_" + (row.item_name || "Report"),
      author: "System",
      subject: "Issue MTC Report",
    },
  };

  pdfMake.createPdf(docDefinition).open();
};

export default generateIssueMTCPdf;